/**
 * Filter Utilities
 *
 * This file contains all filter-related utilities for orders:
 * - Date range filter calculations
 * - Filter label formatters
 *
 * All dates are Unix timestamps in milliseconds (Date.now() format).
 *
 * Note: Filter type definitions and enums are in ../types/filters.ts
 */

import {
	getEndOfThisMonth,
	getEndOfThisWeek,
	getEndOfToday,
	getStartOfNextMonth,
	getStartOfThisMonth,
	getStartOfThisWeek,
	getStartOfToday,
} from '@common/lib/time';

import { DeliveryDateFilter } from '../types/filters';

/**
 * Get date range for delivery date filter
 * Returns { gte, lte } for the filter range, or null if invalid
 */
export function getDeliveryDateRange(
	filter: DeliveryDateFilter,
): { gte: number; lte?: number } | null {
	const startOfToday = getStartOfToday();
	const endOfToday = getEndOfToday();
	const startOfThisWeek = getStartOfThisWeek();
	const endOfThisWeek = getEndOfThisWeek();
	const startOfThisMonth = getStartOfThisMonth();
	const endOfThisMonth = getEndOfThisMonth();
	const startOfNextMonth = getStartOfNextMonth();

	switch (filter) {
		case DeliveryDateFilter.TODAY:
			return { gte: startOfToday, lte: endOfToday };
		case DeliveryDateFilter.THIS_WEEK:
			return { gte: startOfThisWeek, lte: endOfThisWeek };
		case DeliveryDateFilter.NEXT_2_WEEKS: {
			// From end of this week to end of 2 weeks from end of this week
			const endOfNextTwoWeeks = new Date(endOfThisWeek);
			endOfNextTwoWeeks.setDate(endOfNextTwoWeeks.getDate() + 14);
			endOfNextTwoWeeks.setHours(23, 59, 59, 999);
			return { gte: endOfThisWeek + 1, lte: endOfNextTwoWeeks.getTime() };
		}
		case DeliveryDateFilter.THIS_MONTH:
			return { gte: startOfThisMonth, lte: endOfThisMonth };
		case DeliveryDateFilter.NEXT_MONTH_AND_BEYOND:
			// From start of next month onwards (no upper limit)
			return { gte: startOfNextMonth };
		case DeliveryDateFilter.CUSTOM:
			// Custom range is handled separately via custom range atoms
			return null;
		default:
			return null;
	}
}

/**
 * Format delivery date filter label for display
 */
export function formatDeliveryDateFilterLabel(filter: DeliveryDateFilter): string {
	switch (filter) {
		case DeliveryDateFilter.TODAY:
			return 'Today';
		case DeliveryDateFilter.THIS_WEEK:
			return 'This week';
		case DeliveryDateFilter.NEXT_2_WEEKS:
			return 'Next 2 weeks';
		case DeliveryDateFilter.THIS_MONTH:
			return 'This month';
		case DeliveryDateFilter.NEXT_MONTH_AND_BEYOND:
			return 'Next month and beyond';
		case DeliveryDateFilter.CUSTOM:
			return 'Custom';
		default:
			return filter;
	}
}
